import { insuranceMonitorsApi } from "@api";
import {
  InsuranceMonitor,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesInsuranceMonitors } from "../../../../v3/query/queriesInsuranceMonitors";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";

import { getInsuranceMonitorFromCache } from "./configs/helpers/getInsuranceMonitorFromCache";
import { usePayeeInsuranceCoverageConfig } from "./configs/payee/usePayeeInsuranceCoverageConfig";
import { usePayerInsuranceCoverageConfig } from "./configs/payer/usePayerInsuranceCoverageConfig";
import { INSURANCE_MONITOR_EXPAND } from "./constants";

/**
 * Hoisted so the batch read and `buildConfigFields` address the same cache
 * entry — the config resolves a missing document's pending/failed state through
 * this key, and the key includes `expand`, so it must not be spelled twice.
 */
const getDataSourceQueryKey = (id: string) =>
  queriesInsuranceMonitors.getInsuranceMonitor.getQueryKey({
    monitorId: id,
    expand: INSURANCE_MONITOR_EXPAND
  });

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    getDataSourceQueryKey,
    requirements,
    id =>
      insuranceMonitorsApi.getInsuranceMonitor({
        monitorId: id,
        expand: INSURANCE_MONITOR_EXPAND
      }),
    RequirementType.INSURANCE_COVERAGE
  );

  return useWSQueries({ queries });
};

export const useInsuranceCoverageRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.INSURANCE_COVERAGE] => {
  const contextQueries = useContextQueries(requirements);
  const payerInsuranceCoverageConfig =
    usePayerInsuranceCoverageConfig(callback);
  const payeeInsuranceCoverageConfig =
    usePayeeInsuranceCoverageConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // recompute only when the batch query data updates — the payer/payee configs
  // are new objects every render and must not invalidate the memo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<InsuranceMonitor, RequirementType.INSURANCE_COVERAGE>({
      payerConfig: payerInsuranceCoverageConfig,
      payeeConfig: payeeInsuranceCoverageConfig,
      getAdditionalData: getInsuranceMonitorFromCache,
      hasDataSource: true,
      getDataSourceQueryKey
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getInsuranceMonitorFromCache(requirement, context);
    },
    isLoading: contextQueries.some(
      query => !query.isFetched || query.isLoading
    ),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
